// frontend/src/features/wind/WindMapPanel.js
import React, { useMemo } from "react";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Tooltip,
} from "react-leaflet";

/**
 * WindMapPanel — styled shell + Leaflet basemap + fullscreen toggle.
 *
 * Props:
 *  - projects: [{ id, name, state, lat, lon }]
 *  - selectedIds: string[]
 *  - subjectId: string | null
 *  - regionLabelShort: string
 *  - onMarkerClick: (projectId: string, isShift: boolean) => void
 *  - onToggleFullscreen: () => void
 *  - isFullscreen: boolean
 */
export default function WindMapPanel({
  projects,
  selectedIds,
  subjectId,
  regionLabelShort,
  onMarkerClick,
  onToggleFullscreen,
  isFullscreen,
}) {
  // Normalize projects and coerce lat/lon to numbers
  const projectsWithCoords = useMemo(() => {
    const source = Array.isArray(projects) ? projects : [];

    return source
      .map((p) => {
        const latRaw =
          typeof p.lat === "number" ? p.lat : parseFloat(p.lat);
        const lonRaw =
          typeof p.lon === "number" ? p.lon : parseFloat(p.lon);
        const lat = Number.isFinite(latRaw) ? latRaw : null;
        const lon = Number.isFinite(lonRaw) ? lonRaw : null;
        return { ...p, _latNum: lat, _lonNum: lon };
      })
      .filter((p) => p._latNum !== null && p._lonNum !== null);
  }, [projects]);

  const hasPoints = projectsWithCoords.length > 0;

  // Same fallback center/zoom as the working minimal map
  const fallbackCenter = useMemo(() => {
    if (!projectsWithCoords.length) {
      return [37.5, -96.5]; // broad US view
    }
    const latSum = projectsWithCoords.reduce(
      (acc, p) => acc + p._latNum,
      0
    );
    const lonSum = projectsWithCoords.reduce(
      (acc, p) => acc + p._lonNum,
      0
    );
    return [
      latSum / projectsWithCoords.length,
      lonSum / projectsWithCoords.length,
    ];
  }, [projectsWithCoords]);

  const fallbackZoom = useMemo(() => {
    if (!projectsWithCoords.length) {
      return regionLabelShort === "US" ? 3 : 5;
    }
    if (projectsWithCoords.length > 200) return 4;
    if (projectsWithCoords.length > 50) return 5;
    return regionLabelShort === "US" ? 4 : 6;
  }, [projectsWithCoords, regionLabelShort]);

  // The ONLY place we control height is here, like in the minimal test
  const containerHeight = isFullscreen
    ? "h-full"
    : "h-[260px] sm:h-[320px]";

  return (
    <div className="rounded-3xl border border-slate-800 bg-slate-900/70 relative overflow-hidden">
      {/* teal glow + grid, but pointer-events-none so they don't block the map */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(56,189,248,0.25),transparent_55%),radial-gradient(circle_at_bottom,_rgba(16,185,129,0.25),transparent_55%)] opacity-60" />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(15,23,42,0.75)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,0.75)_1px,transparent_1px)] bg-[length:40px_40px]" />

      <div className="relative px-4 sm:px-5 lg:px-6 pt-4 sm:pt-5 lg:pt-6 pb-3 flex flex-col gap-3">
        {/* Header / controls row */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold text-slate-200">
              Map view — {regionLabelShort}
            </div>
            <div className="text-[11px] text-slate-400">
              Each point is a wind farm from USWTDB. Click to inspect; Shift+click
              a point to toggle selection for a Comp Set.
            </div>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-slate-400">
            <button
              type="button"
              onClick={onToggleFullscreen}
              className="rounded-full border border-slate-600 bg-slate-900/70 px-2 py-1 hover:bg-slate-800/80 transition"
            >
              {isFullscreen ? "Exit full screen" : "Full screen map"}
            </button>
            <div className="hidden sm:flex items-center gap-2">
              <LegendDot className="bg-slate-500" label="Farm" />
              <LegendDot className="bg-emerald-400" label="Selected" />
              <LegendDot className="bg-sky-400" label="Subject" />
            </div>
          </div>
        </div>

        {/* Map container – this is the same layout as the minimal working test */}
        <div
          className={`border border-slate-700 rounded-2xl overflow-hidden bg-slate-950/70 ${containerHeight}`}
        >
          {!hasPoints ? (
            <div className="w-full h-full grid place-items-center text-center px-4">
              <div className="max-w-xs">
                <div className="text-xs font-semibold text-slate-200 mb-1.5">
                  No coordinates in current view
                </div>
                <div className="text-[11px] text-slate-400">
                  Try adjusting filters (region/capacity/vintage). Once farms
                  with lat/lon are in view, we’ll show them here on the map.
                </div>
              </div>
            </div>
          ) : (
            <MapContainer
              center={fallbackCenter}
              zoom={fallbackZoom}
              style={{ width: "100%", height: "100%" }}
              zoomControl={true}
            >
              {/* OSM tiles for stability; you can swap these for Esri+Carto after this is solid */}
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="&copy; OpenStreetMap contributors"
              />

              {projectsWithCoords.map((p) => {
                const isSelected = selectedIds?.includes(p.id);
                const isSubject = subjectId === p.id;
                const radius = isSubject ? 7 : 5;
                const color = isSubject
                  ? "#38bdf8"
                  : isSelected
                  ? "#22c55e"
                  : "#facc15";

                return (
                  <CircleMarker
                    key={p.id}
                    center={[p._latNum, p._lonNum]}
                    radius={radius}
                    pathOptions={{
                      color,
                      fillColor: color,
                      fillOpacity: isSubject ? 0.9 : isSelected ? 0.8 : 0.7,
                      weight: isSubject ? 2 : isSelected ? 1.5 : 1,
                    }}
                    eventHandlers={{
                      click: (e) => {
                        const isShift =
                          e.originalEvent && e.originalEvent.shiftKey;
                        onMarkerClick?.(p.id, !!isShift);
                      },
                    }}
                  >
                    <Tooltip direction="top" offset={[0, -4]} opacity={0.95}>
                      <div className="text-[11px]">
                        <div className="font-semibold">
                          {p.name || "Unnamed project"}
                        </div>
                        <div className="text-slate-300">
                          {p.state || "State"} • {p._latNum.toFixed(3)},{" "}
                          {p._lonNum.toFixed(3)}
                        </div>
                      </div>
                    </Tooltip>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          )}
        </div>
      </div>
    </div>
  );
}

function LegendDot({ className, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className={`h-2 w-2 rounded-full inline-block ${className}`}
      ></span>
      <span>{label}</span>
    </span>
  );
}
